import * as React from 'react';

// Local stand-ins for the builder's shadcn primitives and admin Surface, styled with the
// builder's own --dash-* tokens so the ported panels look the same as in the builder.
export const cn = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(' ');

export const DASH_CSS = `
.aa-dash{
  --dash-bg:#f7f7f5;--dash-bg-subtle:#f0f0ee;--dash-bg-muted:#ebebe8;
  --dash-text:#18191d;--dash-text-secondary:#3a3d44;--dash-text-muted:#6c7079;
  --dash-border:#e7e7e4;--dash-primary:#18191d;--dash-primary-tint:#f0f0ee;--dash-panel-bg:#ffffff;
  --dash-switch-on:hsl(218 87% 60%);--dash-input:hsl(240 5.9% 90%);--dash-accent:hsl(55 100% 50%);
  color:var(--dash-text);
}
.cd-root[data-cd-theme="dark"] .aa-dash{
  --dash-bg:#18191d;--dash-bg-subtle:#383838;--dash-bg-muted:#333333;
  --dash-text:#f5f5f5;--dash-text-secondary:#b9b9c0;--dash-text-muted:#8a8a95;
  --dash-border:#4d4d4d;--dash-primary:#5A93F5;--dash-primary-tint:rgba(90,147,245,0.14);--dash-panel-bg:#282828;
  --dash-switch-on:hsl(218 80% 66%);--dash-input:hsl(222 11% 18%);
}
`;

export function Surface({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('rounded-[13px] border border-[var(--dash-border)] bg-[var(--dash-panel-bg)]', className)}>{children}</div>;
}

export function SurfaceHeader({ title, className }: { title: React.ReactNode; className?: string }) {
  return (
    <div className={cn('flex items-center gap-3 border-b border-[var(--dash-border)] px-[18px] py-3.5', className)}>
      <h3 className="text-[15.5px] font-bold tracking-[-0.01em] text-[var(--dash-text)]">{title}</h3>
    </div>
  );
}

const TabsCtx = React.createContext<{ value: string; setValue: (v: string) => void }>({ value: '', setValue: () => {} });

export function Tabs({ value, defaultValue, onValueChange, className, children }: {
  value?: string; defaultValue?: string; onValueChange?: (v: string) => void; className?: string; children: React.ReactNode;
}) {
  const [inner, setInner] = React.useState(defaultValue || '');
  const current = value ?? inner;
  const setValue = (v: string) => {
    setInner(v);
    onValueChange?.(v);
  };
  return <TabsCtx.Provider value={{ value: current, setValue }}><div className={className}>{children}</div></TabsCtx.Provider>;
}

export function TabsList({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <div role="tablist" className={cn('inline-flex h-10 items-center justify-center rounded-md bg-[var(--dash-bg-subtle)] p-1 text-[var(--dash-text-muted)]', className)}>
      {children}
    </div>
  );
}

export function TabsTrigger({ value, className, children }: { value: string; className?: string; children: React.ReactNode }) {
  const ctx = React.useContext(TabsCtx);
  const active = ctx.value === value;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={() => ctx.setValue(value)}
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all',
        active && 'bg-[var(--dash-panel-bg)] text-[var(--dash-text)] shadow-sm',
        className,
      )}
    >
      {children}
    </button>
  );
}

export function TabsContent({ value, className, children }: { value: string; className?: string; children: React.ReactNode }) {
  const ctx = React.useContext(TabsCtx);
  if (ctx.value !== value) return null;
  return <div role="tabpanel" className={cn('mt-2', className)}>{children}</div>;
}

export function Switch({ checked, disabled, onCheckedChange, className, ...rest }: {
  checked: boolean; disabled?: boolean; onCheckedChange?: (v: boolean) => void; className?: string;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'>) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onCheckedChange?.(!checked)}
      className={cn(
        'peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'bg-[var(--dash-switch-on)]' : 'bg-[var(--dash-input)]',
        className,
      )}
      {...rest}
    >
      <span className={cn('pointer-events-none block h-5 w-5 rounded-full bg-white shadow-lg ring-0 transition-transform', checked ? 'translate-x-5' : 'translate-x-0')} />
    </button>
  );
}

export const Button = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'outline'; size?: 'sm' }>(
  ({ className, variant, size, type, ...props }, ref) => (
    <button
      ref={ref}
      type={type || 'button'}
      className={cn(
        'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50',
        'border border-[var(--dash-input)] bg-[var(--dash-panel-bg)] text-[var(--dash-text)] shadow-sm hover:bg-[var(--dash-accent)] hover:text-[#111111]',
        'h-8 px-3 text-xs',
        className,
      )}
      {...props}
    />
  ),
);
Button.displayName = 'Button';

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className, type, ...props }, ref) => (
    <input
      ref={ref}
      type={type}
      className={cn(
        'flex h-9 w-full rounded-md border border-[var(--dash-input)] bg-transparent px-3 py-1 text-sm text-[var(--dash-text)] shadow-sm transition-colors placeholder:text-[var(--dash-text-muted)] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    />
  ),
);
Input.displayName = 'Input';
